class MeetOrganizedDaySlider extends HTMLElement {
  constructor() {
    super();
    this._defined = false;
    this._activeIndex = -1;
  }

  connectedCallback() {
    if (this._defined) return;
    this._defined = true;

    this.viewport = this.querySelector('[data-viewport]');
    if (!this.viewport) return;

    this.slides = this.querySelectorAll('[data-slide]');
    this.prevBtn = this.querySelector('[data-prev]');
    this.nextBtn = this.querySelector('[data-next]');
    this.progress = this.querySelector('[data-progress-bar]');
    this.counter = this.querySelector('[data-counter]');

    this.autoplay = this.dataset.autoplay === 'true';
    this.speed = parseInt(this.dataset.autoplaySpeed || '5000', 10);
    this.total = this.slides.length;
    this.current = 0;
    this._step = 0;
    this._positions = [];
    this._timer = null;
    this._observer = null;
    this._sectionVisible = false;

    this._measure();
    this._bind();
    this._update();
    this._observeVisibility();

    if (this.autoplay && this.total > 1) this._startAutoplay();
  }

  disconnectedCallback() {
    this._stopAutoplay();
    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
    }
  }

  _measure() {
    if (!this.viewport || this.total === 0) return;
    const first = this.slides[0];
    if (!first) return;

    const firstLeft = first.offsetLeft;
    this._positions = Array.from(this.slides).map((slide) => slide.offsetLeft - firstLeft);
    this._step = this._positions.length > 1 ? this._positions[1] - this._positions[0] : first.offsetWidth;
  }

  _bind() {
    this.prevBtn?.addEventListener('click', () => {
      this.goTo(this.current - 1);
      this._restartAutoplay();
    });
    this.nextBtn?.addEventListener('click', () => {
      this.goTo(this.current + 1);
      this._restartAutoplay();
    });

    this.viewport.addEventListener('scroll', () => this._onScroll(), { passive: true });

    window.addEventListener('resize', () => {
      this._measure();
      this.goTo(this.current, false);
    });

    this.tabIndex = 0;
    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        this.goTo(this.current - 1);
        this._restartAutoplay();
      } else if (e.key === 'ArrowRight') {
        this.goTo(this.current + 1);
        this._restartAutoplay();
      }
    });
  }

  _observeVisibility() {
    if (!('IntersectionObserver' in window)) {
      this._replayActiveSlide();
      return;
    }

    this._observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const visible = entry.isIntersecting && entry.intersectionRatio >= 0.28;
        if (visible && !this._sectionVisible) {
          this._sectionVisible = true;
          this._measure();
          this._onScroll();
          this._replayActiveSlide();
        } else if (!visible) {
          this._sectionVisible = false;
        }
      });
    }, {
      threshold: [0, 0.28, 0.55],
      rootMargin: '0px 0px -8% 0px',
    });

    this._observer.observe(this);
  }

  _onScroll() {
    if (!this._positions.length) return;
    const left = this.viewport.scrollLeft;
    let nearestIndex = 0;
    let nearestDistance = Infinity;

    this._positions.forEach((position, index) => {
      const distance = Math.abs(left - position);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestIndex = index;
      }
    });

    this.current = Math.max(0, Math.min(this.total - 1, nearestIndex));
    this._update();
  }

  goTo(index, smooth = true) {
    if (index < 0) index = 0;
    if (index >= this.total) index = this.total - 1;
    this.current = index;
    const left = this._positions[this.current] ?? this.current * this._step;
    this.viewport.scrollTo({ left, behavior: smooth ? 'smooth' : 'auto' });
    this._update();
  }

  _update() {
    const pct = this.total > 0 ? ((this.current + 1) / this.total) * 100 : 0;
    if (this.progress) this.progress.style.width = `${pct}%`;
    if (this.counter) {
      this.counter.textContent = `${String(this.current + 1).padStart(2, '0')} / ${String(this.total).padStart(2, '0')}`;
    }
    if (this.prevBtn) this.prevBtn.disabled = this.current === 0;
    if (this.nextBtn) this.nextBtn.disabled = this.current === this.total - 1;
    this._setActiveSlide();
  }

  _setActiveSlide(force = false) {
    if (!force && this._activeIndex === this.current) return;

    this.slides.forEach((slide) => {
      slide.classList.remove('is-active');
      slide.querySelectorAll('[data-count-to]').forEach((node) => {
        node.textContent = '0';
      });
    });

    const activeSlide = this.slides[this.current];
    if (!activeSlide) return;

    // Force animation replay when slide becomes active again.
    void activeSlide.offsetWidth;
    activeSlide.classList.add('is-active');
    this._animateCounters(activeSlide);
    this._activeIndex = this.current;
  }

  _replayActiveSlide() {
    this._setActiveSlide(true);
  }

  _animateCounters(slide) {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || this.classList.contains('meet-organized-day--reduce-motion')) {
      slide.querySelectorAll('[data-count-to]').forEach((node) => {
        node.textContent = node.dataset.countTo || '0';
      });
      return;
    }

    slide.querySelectorAll('[data-count-to]').forEach((node) => {
      const target = parseInt(node.dataset.countTo || '0', 10);
      const duration = parseInt(node.dataset.countDuration || '800', 10);
      const delay = parseInt(node.dataset.countDelay || '900', 10);
      const mobileUiDelay = window.matchMedia('(max-width: 767px)').matches ? 3000 : 0;
      const start = performance.now() + delay + mobileUiDelay;

      const tick = (now) => {
        if (now < start) {
          requestAnimationFrame(tick);
          return;
        }
        const progress = Math.min((now - start) / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        node.textContent = Math.round(target * eased).toString();
        if (progress < 1) requestAnimationFrame(tick);
      };

      node.textContent = '0';
      requestAnimationFrame(tick);
    });
  }

  _startAutoplay() {
    this._stopAutoplay();
    this._timer = setInterval(() => {
      if (this.current >= this.total - 1) {
        this.goTo(0);
      } else {
        this.goTo(this.current + 1);
      }
    }, this.speed);
  }

  _stopAutoplay() {
    if (this._timer) {
      clearInterval(this._timer);
      this._timer = null;
    }
  }

  _restartAutoplay() {
    if (this.autoplay) this._startAutoplay();
  }
}

if (!customElements.get('meet-organized-day-slider')) {
  customElements.define('meet-organized-day-slider', MeetOrganizedDaySlider);
}
